import { API_URL } from "../utils/graphqlRequest";
import {
  COMPLETE_UPLOAD_MUTATION,
  GET_PART_URLS_MUTATION,
  POLL_ASSET_QUERY,
  START_UPLOAD_MUTATION,
} from "../constants/queries";

export const uploadVideoMultipart = async (
  file: File,
  showError: Function
): Promise<{ publicUrl: string; posterUrl: string } | null> => {
  const token = localStorage.getItem("authToken");
  const filename = file.name;
  const sizeBytes = file.size;

  let fileBuffer: ArrayBuffer;
  try {
    fileBuffer = await file.arrayBuffer();
  } catch {
    showError("Upload Error", "Could not read file buffer.");
    return null;
  }

  const sha256 = async (input: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  const hash = await sha256(filename + sizeBytes.toString());

  // 1. Start upload session
  const startRes = await fetch(API_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: START_UPLOAD_MUTATION,
      variables: {
        videoType: "workout_info_video",
        filename,
        sizeBytes,
        sha256: hash,
        parts: 6,
      },
    }),
  });

  const startData = await startRes.json();
  if (startData.errors?.length) {
    showError("Upload Error", startData.errors[0].message);
    return null;
  }

  const session = startData.data?.startVideoUpload;
  if (!session) {
    showError("Upload Error", "Failed to start upload session.");
    return null;
  }

  const { sessionId, maxPartSize, partUrls } = session;
  const totalParts = Math.ceil(sizeBytes / maxPartSize);

  const urlMap = new Map<number, string>();
  for (const { partNumber, url } of partUrls) {
    urlMap.set(partNumber, url);
  }

  const partETags: [string, string][] = [];

  // 2. Upload all parts
  for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
    if (!urlMap.has(partNumber)) {
      const getPartRes = await fetch(API_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: GET_PART_URLS_MUTATION,
          variables: {
            sessionId,
            parts: [partNumber],
          },
        }),
      });

      const getPartData = await getPartRes.json();
      if (
        getPartData.errors?.length ||
        !getPartData.data?.getPartUrls?.length
      ) {
        showError("Upload Error", `Failed to get URL for part ${partNumber}.`);
        return null;
      }

      const { partNumber: pn, url } = getPartData.data.getPartUrls[0];
      urlMap.set(pn, url);
    }

    const start = (partNumber - 1) * maxPartSize;
    const end = Math.min(start + maxPartSize, file.size);
    const chunk = file.slice(start, end);
    const url = urlMap.get(partNumber)!;

    const res = await fetch(url, {
      method: "PUT",
      body: chunk,
    });

    if (!res.ok) {
      showError("Upload Error", `Failed to upload part ${partNumber}.`);
      return null;
    }

    const eTag = res.headers.get("ETag")?.replace(/"/g, "");
    if (!eTag) {
      showError("Upload Error", `Missing ETag for part ${partNumber}.`);
      return null;
    }

    partETags.push([`${partNumber}`, eTag]);
  }

  // 3. Complete upload
  const completeRes = await fetch(API_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: COMPLETE_UPLOAD_MUTATION,
      variables: {
        sessionId,
        parts: partETags,
      },
    }),
  });

  const completeData = await completeRes.json();
  if (completeData.errors?.length) {
    showError("Upload Error", completeData.errors[0].message);
    return null;
  }

  const asset = completeData.data?.completeUpload;
  if (!asset?.id) {
    showError("Upload Error", "Upload completion failed.");
    return null;
  }

  // 4. Poll for READY
  const pollAssetStatus = async (): Promise<any> => {
    const interval = 10000;

    while (true) {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: POLL_ASSET_QUERY,
          variables: { id: asset.id },
        }),
      });

      const data = await res.json();

      if (data.errors?.length) {
        showError("Upload Error", data.errors[0].message);
        return null;
      }

      const status = data.data?.videoAsset?.status;
      console.log("Status:", status);

      if (status === "READY") {
        return data.data.videoAsset;
      } else if (status === "FAILED") {
        showError("Upload Error", "Video processing failed.");
        return null;
      }

      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  };

  const finalAsset = await pollAssetStatus();
  if (!finalAsset?.publicUrl) {
    showError("Upload Error", "Missing public URL.");
    return null;
  }

  return {
    publicUrl: finalAsset.publicUrl,
    posterUrl: finalAsset.posterUrl,
  };
};
